import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Business, User } from '../types';
import FilteringEngine from '../utils/filteringEngine';
import { Star, MapPin, Sparkles } from 'lucide-react';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MAP_PIN_URL = '/map-pin.svg';

const businessIcon = new L.Icon({
  iconUrl: MAP_PIN_URL,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
  popupAnchor: [0, -32],
});

interface MapViewProps {
  onBusinessSelect: (business: Business) => void;
  selectedBusiness: Business | null;
  businesses?: Business[];
  user?: User;
}

const MapView: React.FC<MapViewProps> = ({ onBusinessSelect, selectedBusiness, businesses: propBusinesses, user }) => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const [mapCenter] = useState<[number, number]>([40.7128, -74.0060]); // New York City


  // Use prop businesses if provided, otherwise use sample data
  useEffect(() => {
    if (propBusinesses) {
      setBusinesses(propBusinesses);
    } else {
      // Sample business data - in a real app, this would come from an API
      const sampleBusinesses: Business[] = [
        {
          id: '1',
          name: 'Artisan Coffee Roasters',
          address: '123 Main St, New York, NY 10001',
          latitude: 40.7128,
          longitude: -74.0060,
          category: 'Coffee Shop',
          interestCategories: ['food_dining'],
          tags: ['coffee', 'cozy', 'wifi', 'study', 'artisan', 'roasted'],
          priceRange: '$$',
          phone: '(555) 123-4567',
          website: 'www.artisancoffee.com',
          description: 'Premium coffee roasted fresh daily with a cozy atmosphere perfect for work or relaxation.',
          averageRating: 4.8,
          totalReviews: 127,
          features: ['WiFi', 'Pet Friendly', 'Outdoor Seating']
        },
        {
          id: '2',
          name: 'Green Garden Bistro',
          address: '456 Park Ave, New York, NY 10016',
          latitude: 40.7589,
          longitude: -73.9851,
          category: 'Restaurant',
          interestCategories: ['food_dining', 'health_wellness'],
          tags: ['organic', 'farm-to-table', 'healthy', 'local', 'fresh'],
          priceRange: '$$$',
          phone: '(555) 987-6543',
          website: 'www.greengardenbistro.com',
          description: 'Farm-to-table dining experience with fresh, locally sourced ingredients.',
          averageRating: 4.6,
          totalReviews: 89,
          features: ['Vegan Options', 'Gluten Free', 'Reservations']
        },
        {
          id: '3',
          name: 'Tech Repair Pro',
          address: '789 Broadway, New York, NY 10003',
          latitude: 40.7282,
          longitude: -73.9942,
          category: 'Electronics Repair',
          interestCategories: ['technology', 'services_professional'],
          tags: ['repair', 'electronics', 'fast', 'certified', 'warranty'],
          priceRange: '$$',
          phone: '(555) 456-7890',
          description: 'Fast and reliable electronics repair service with certified technicians.',
          averageRating: 4.9,
          totalReviews: 203,
          features: ['Same Day Service', 'Warranty', 'Free Diagnosis']
        },
        {
          id: '4',
          name: 'Sunset Yoga Studio',
          address: '321 West St, New York, NY 10014',
          latitude: 40.7353,
          longitude: -74.0103,
          category: 'Fitness & Wellness',
          interestCategories: ['health_wellness', 'sports_fitness'],
          tags: ['yoga', 'meditation', 'wellness', 'peaceful', 'classes'],
          priceRange: '$$',
          phone: '(555) 234-5678',
          website: 'www.sunsetyoga.com',
          description: 'Peaceful yoga studio offering classes for all levels with experienced instructors.',
          averageRating: 4.7,
          totalReviews: 156,
          features: ['All Levels', 'Meditation', 'Peaceful Environment']
        }
      ];
      setBusinesses(sampleBusinesses);
    }
  }, [propBusinesses]);

  // App.tsx already applies interest/preference filtering before passing businesses
  // as props, so we just use what was passed in directly.
  useEffect(() => {
    setFilteredBusinesses(businesses);
  }, [businesses]);

  const MapEvents = () => {
    useMapEvents({
      click: () => {
        // Close business details when clicking on map
        if (selectedBusiness) {
          onBusinessSelect(null as any);
        }
      },
    });
    return null;
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) 
            ? 'text-yellow-400 fill-current' 
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={mapCenter}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapEvents />
        
        {filteredBusinesses.map((business) => {
          const relevanceScore = user && user.interests ? 
            FilteringEngine.calculateBusinessRelevance(business, user.interests) : 0;
          const isHighlyRelevant = relevanceScore >= 7;
          
          return (
          <Marker
            key={business.id}
            position={[business.latitude, business.longitude]}
            icon={businessIcon}
            eventHandlers={{
              click: () => onBusinessSelect(business),
            }}
          >
            <Popup>
              <div className="p-2 min-w-[250px]">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">{business.name}</h3>
                  <div className="flex items-center space-x-1 ml-2">
                    {renderStars(business.averageRating || 0)}
                    <span className="text-xs text-gray-600 ml-1">
                      ({business.totalReviews})
                    </span>
                  </div>
                </div>
                
                <p className="text-xs text-gray-600 mb-2">{business.category}</p>
                
                <div className="flex items-start space-x-1 mb-3">
                  <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-600">{business.address}</span>
                </div>
                
                {business.description && (
                  <p className="text-xs text-gray-700 mb-3 line-clamp-2">
                    {business.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onBusinessSelect(business);
                    }}
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium py-2 px-3 rounded-md transition-colors duration-200 mr-2"
                  >
                    View Full Profile
                  </button>
                  {isHighlyRelevant && (
                    <div className="flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md">
                      <Sparkles className="w-3 h-3" />
                      <span className="text-xs ml-1">Match</span>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    {business.phone && (
                      <a
                        href={`tel:${business.phone}`}
                        className="text-xs text-primary-600 hover:text-primary-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Call
                      </a>
                    )}
                    {business.website && (
                      <a
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Website
                      </a>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-600">
                    {business.priceRange && (
                      <span className="font-medium">{business.priceRange}</span>
                    )}
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>

      {/* Map Controls */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-white rounded-lg shadow-md p-3">
          <div className="flex items-center space-x-2 text-sm text-gray-700">
            <MapPin className="w-4 h-4 text-primary-600" />
            <span className="font-medium">Discover Businesses</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Click on markers to see details
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-10">
        <div className="bg-white rounded-lg shadow-md p-3">
          <div className="flex items-center space-x-2">
            <img src={MAP_PIN_URL} alt="" className="h-5 w-auto" />
            <span className="text-xs text-gray-700">Business Location</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapView;
